import { createDecorator } from "../../instantiation/common/registry.js";
import { IKeyboardEvent } from "../../../base/browser/keyboardEvent.js";
import { IDisposable } from "../../../base/common/lifecycle.js";

export type KeyCode = ({} & string); // e.g., "KeyA", "ArrowUp", "Digit1", etc.
export type ModifierKey = "Shift" | "ShiftLeft" | "Control" | "ControlLeft" | "Alt" | "AltLeft" | "Meta" | "MetaLeft";

export const isModifierKey = (key: string): key is ModifierKey => {
    switch(key) {
        case "Shift":
        case "Control":
        case "Alt":
        case "Meta":
        case "ShiftLeft":
        case "ControlLeft":
        case "AltLeft":
        case "MetaLeft":
            return true;
    }
    return false;
}

export const IKeyboardService = createDecorator<IKeyboardService>('keyboardService');

export interface KeyboardListenerOptions {
    /**
     * Whether delivery respects the `scopeEventsToRegisteredRoots` filter
     * (chrome-extension product). `true` (the DEFAULT) delivers only events whose
     * composed path crosses a registered editor root — the right choice for
     * keybindings / commands, so typing into the HOST page never triggers them.
     * `false` delivers EVERY key, ignoring scope: for input that must react
     * regardless of what's focused — e.g. the fly camera swallowing nav keys while
     * flying a third-party page whose canvas has focus (the scoped stream stays
     * silent there, so it could never `stopPropagation` the host's handlers).
     * `isKeyPressed` state is always unscoped regardless of this option.
     */
    scoped?: boolean;
}

/** A key event plus the window it was observed in — the service listens in EVERY
 *  open window, so a consumer that owns something in ONE window (a render
 *  viewport) can tell a popout's keys apart without digging through the DOM. */
export interface IKeyboardEventWithSource extends IKeyboardEvent {
    readonly sourceWindow: Window;
}

export type KeyboardListener = (event: IKeyboardEventWithSource) => void;

export interface KeyPressedOptions {
    /**
     * Only report the key as pressed while this window has focus. Pressed-key
     * state is global on purpose (a key held in the host page must still register,
     * and listening everywhere is what prevents stuck keys when focus moves
     * mid-press) — but a consumer driving ONE window's viewport wants "held AND
     * meant for me", which is this.
     */
    window?: Window | null;
}

export interface IKeyboardService {
    isKeyPressed(key: KeyCode | ModifierKey, options?: KeyPressedOptions): boolean;

    onKeyDown(listener: KeyboardListener, options?: KeyboardListenerOptions): IDisposable;
    onKeyUp(listener: KeyboardListener, options?: KeyboardListenerOptions): IDisposable;
    onKeyPress(listener: KeyboardListener, options?: KeyboardListenerOptions): IDisposable;

    /**
     * Registers an element as a keyboard SCOPE root. Only relevant when the service was
     * created with `scopeEventsToRegisteredRoots` (the chrome-extension product): key
     * EVENTS (which drive keybindings/commands) are then emitted only for events whose
     * composed path crosses a registered root — typing into the HOST page never triggers
     * editor commands. Products that own the whole document (pwa) never register roots
     * and the option is off, so every key event is emitted.
     *
     * `isKeyPressed` state is NEVER scoped — pressed-modifier/WASD state must track the
     * host window too (fly controls, shift-multiselect while the canvas has focus).
     */
    registerScopeRoot(root: HTMLElement): IDisposable;

    /**
     * Whether this event would be delivered on the SCOPED stream (its composed path
     * crosses a registered scope root — or scoping is off entirely, as in the pwa).
     * Lets an unscoped subscriber act only on events the scoped pipeline will NOT
     * see (e.g. canvas-shortcut delivery while the host page has focus) without
     * double-handling in-scope keys.
     */
    isEventInScope(event: KeyboardEvent): boolean;
}
